import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  ActionModulesDoneType,
  ActivatedAccountsType,
  AveragePointType,
  ChecklistAnswerStreakType,
  ControlSignatureMonthlyType,
  ControlSignatureWeeklyType, DropdownAnswerStreakType,
  EvaluationStarsType,
  EvaluationThumbsType,
  PresenceQuotaType,
  TotalPointType,
  UpcomingAnniversaryType,
  UpcomingBirthdayType
} from "../../types/reportingTypes";
import { reportingApi } from "../../api/reportingApi";
import { AppDispatch, RootState } from "../store";
import { handleRequest } from "../utils/handleRequest";

export const fetchAveragePointThunk = createAsyncThunk<
  AveragePointType[],
  { year: number; month: number; take: number },
  { dispatch: AppDispatch; state: RootState }
>("reporting/averagePoints", async ({ year, month, take }, { dispatch }) => {
  const res = await handleRequest<AveragePointType[]>(
    dispatch,
    "indirect",
    () => reportingApi.getAveragePoints(year, month, take)
  );

  if (res.success() && res.result) {
    return res.result;
  }

  throw new Error(res.errorMessage ?? "Failed to fetch average points");
});

export const fetchTotalPointThunk = createAsyncThunk<
  TotalPointType[],
  { take: number },
  { dispatch: AppDispatch; state: RootState }
>("reporting/totalPoints", async ({ take }, { dispatch }) => {
  const res = await handleRequest<TotalPointType[]>(
    dispatch,
    "indirect",
    () => reportingApi.getTotalPoints(take)
  );
  if (res.success() && res.result) return res.result;
  throw new Error(res.errorMessage ?? "Failed to fetch total points");
});

export const fetchActionModulesDoneThunk = createAsyncThunk<
  ActionModulesDoneType[],
  { take: number },
  { dispatch: AppDispatch; state: RootState }
>("reporting/actionModulesDone", async ({ take }, { dispatch }) => {
  const res = await handleRequest<ActionModulesDoneType[]>(
    dispatch,
    "indirect",
    () => reportingApi.getActionModulesDone(take)
  );
  if (res.success() && res.result) return res.result;
  throw new Error(res.errorMessage ?? "Failed to fetch action modules done");
});

export const fetchPresenceQuotaThunk = createAsyncThunk<
  PresenceQuotaType[],
  { year: number; month: number },
  { dispatch: AppDispatch; state: RootState }
>("reporting/presenceQuota", async ({ year, month }, { dispatch }) => {
  const res = await handleRequest<PresenceQuotaType[]>(
    dispatch,
    "indirect",
    () => reportingApi.getPresenceQuota(year, month)
  );
  if (res.success() && res.result) return res.result;
  throw new Error(res.errorMessage ?? "Failed to fetch presence quota");
});

export const fetchControlSignatureMonthlyThunk = createAsyncThunk<
  ControlSignatureMonthlyType[],
  { year: number; month: number },
  { dispatch: AppDispatch; state: RootState }
>("reporting/controlSignatureMonthly", async ({ year, month }, { dispatch }) => {
  const res = await handleRequest<ControlSignatureMonthlyType>(
    dispatch,
    "indirect",
    () => reportingApi.getControlSignatureMonthly(year, month)
  );
  if (res.success() && res.result) return [res.result];
  throw new Error(res.errorMessage ?? "Failed to fetch control signature monthly");
});

export const fetchControlSignatureWeeklyThunk = createAsyncThunk<
  ControlSignatureWeeklyType[],
  { year: number; month: number },
  { dispatch: AppDispatch; state: RootState }
>("reporting/controlSignatureWeekly", async ({ year, month }, { dispatch }) => {
  const res = await handleRequest<ControlSignatureWeeklyType[]>(
    dispatch,
    "indirect",
    () => reportingApi.getControlSignatureWeekly(year, month)
  );
  if (res.success() && res.result) return res.result;
  throw new Error(res.errorMessage ?? "Failed to fetch control signature weekly");
});

export const fetchActivatedAccountsThunk = createAsyncThunk<
  ActivatedAccountsType[],
  void,
  { dispatch: AppDispatch; state: RootState }
>("reporting/activatedAccounts", async (_, { dispatch }) => {
  const res = await handleRequest<ActivatedAccountsType>(
    dispatch,
    "indirect",
    () => reportingApi.getActivatedAccounts()
  );
  if (res.success() && res.result) return [res.result];
  throw new Error(res.errorMessage ?? "Failed to fetch activated accounts");
});

export const fetchEvaluationStarsThunk = createAsyncThunk<
  EvaluationStarsType[],
  { startDate: string; endDate: string },
  { dispatch: AppDispatch; state: RootState }
>("reporting/evaluationStars", async ({ startDate, endDate }, { dispatch }) => {
  const res = await handleRequest<EvaluationStarsType>(
    dispatch,
    "indirect",
    () => reportingApi.getEvaluationStars(startDate, endDate)
  );
  if (res.success() && res.result) return [res.result];
  throw new Error(res.errorMessage ?? "Failed to fetch evaluation stars");
});

export const fetchEvaluationThumbsThunk = createAsyncThunk<
  EvaluationThumbsType[],
  { actionModuleId: number; startDate: string; endDate: string },
  { dispatch: AppDispatch; state: RootState }
>("reporting/evaluationThumbs", async ({ actionModuleId, startDate, endDate }, { dispatch }) => {
  const res = await handleRequest<EvaluationThumbsType[]>(
    dispatch,
    "indirect",
    () => reportingApi.getEvaluationThumbs(actionModuleId, startDate, endDate)
  );
  if (res.success() && res.result) return res.result;
  throw new Error(res.errorMessage ?? "Failed to fetch evaluation thumbs");
});

export const fetchUpcomingAnniversariesThunk = createAsyncThunk<
  UpcomingAnniversaryType,
  void,
  { dispatch: AppDispatch; state: RootState }
>("reporting/upcomingAnniversaries", async (_, { dispatch }) => {
  const res = await handleRequest<UpcomingAnniversaryType>(
    dispatch,
    "indirect",
    () => reportingApi.getUpcomingAnniversaries()
  );
  if (res.success() && res.result) return res.result;
  throw new Error(res.errorMessage ?? "Failed to fetch upcoming anniversaries");
});

export const fetchUpcomingBirthdaysThunk = createAsyncThunk<
  UpcomingBirthdayType,
  void,
  { dispatch: AppDispatch; state: RootState }
>("reporting/upcomingBirthdays", async (_, { dispatch }) => {
  const res = await handleRequest<UpcomingBirthdayType>(
    dispatch,
    "indirect",
    () => reportingApi.getUpcomingBirthdays()
  );
  if (res.success() && res.result) return res.result;
  throw new Error(res.errorMessage ?? "Failed to fetch upcoming birthdays");
});

export const fetchChecklistAnswerStreaksThunk = createAsyncThunk<
  ChecklistAnswerStreakType[],
  void,
  { dispatch: AppDispatch; state: RootState }
>("reporting/checklistAnswerStreak", async (_, { dispatch }) => {
  const res = await handleRequest<ChecklistAnswerStreakType[]>(
    dispatch,
    "indirect",
    () => reportingApi.getChecklistAnswerStreak()
  );
  if (res.success() && res.result) return res.result;
  throw new Error(res.errorMessage ?? "Failed to fetch checklist answer streaks");
});

export const fetchDropdownAnswerStreaksThunk = createAsyncThunk<
    DropdownAnswerStreakType[],
  void,
  { dispatch: AppDispatch; state: RootState }
>("reporting/dropdownAnswerStreak", async (_, { dispatch }) => {
  const res = await handleRequest<DropdownAnswerStreakType[]>(
    dispatch,
    "indirect",
    () => reportingApi.getDropdownAnswerStreak()
  );
  if (res.success() && res.result) return res.result;
  throw new Error(res.errorMessage ?? "Failed to fetch dropdown answer streaks");
});